/**
 * Hjelpefunksjoner for innsikt-overlays (fase C).
 * Aggregerer bin-verdier fra serveren til per-reol-intensitet og
 * gir én felles fargeskala for både 2D-canvas og 3D-scenen.
 */

import type { ServerWarehouseInsights } from '../../../../../services/warehouseLayoutService';

/** Nøytral grå for lokasjoner uten data (value=null / ikke i responsen) */
export const INSIGHT_UNKNOWN_COLOR = '#6b7280';

/**
 * Aggregert intensitet per reol (0–1) for fargelegging av hele reol-boksen.
 * - heatmap: MAKS av bin-verdiene (én varm lokasjon skal synes)
 * - empty: SNITT = ANDEL tomme (verdiene er 0/1) — MAKS ville farget alle reoler
 *   røde siden praktisk talt hver reol har minst én tom plass (verifisert i GUI)
 * - fill/dead/countage: SNITT av ikke-null-verdiene (tilstand for reolen som helhet)
 * Bins med value=null ignoreres i begge tilfeller.
 */
export function buildRackIntensity(insights: ServerWarehouseInsights | null): Map<string, number> {
  const result = new Map<string, number>();
  if (!insights) return result;

  const useMax = insights.mode === 'heatmap';

  if (useMax) {
    for (const bin of insights.bins) {
      if (!bin.rackNumber || bin.value === null || bin.value === undefined) continue;
      result.set(bin.rackNumber, Math.max(result.get(bin.rackNumber) ?? 0, bin.value));
    }
    return result;
  }

  // Snitt av ikke-null-verdier per reol
  const accumulator = new Map<string, { sum: number; count: number }>();
  for (const bin of insights.bins) {
    if (!bin.rackNumber || bin.value === null || bin.value === undefined) continue;
    const acc = accumulator.get(bin.rackNumber) ?? { sum: 0, count: 0 };
    acc.sum += bin.value;
    acc.count += 1;
    accumulator.set(bin.rackNumber, acc);
  }
  accumulator.forEach((acc, rackNumber) => {
    result.set(rackNumber, acc.sum / acc.count);
  });
  return result;
}

/**
 * Direkte oppslag binCode → verdi (0–1 eller null=ukjent) — brukes for
 * enkeltlokasjoner (gulv/vegg/heng/skap/dock) som rendres per bin.
 */
export function buildBinValueLookup(insights: ServerWarehouseInsights | null): Map<string, number | null> {
  const result = new Map<string, number | null>();
  if (!insights) return result;
  for (const bin of insights.bins) {
    result.set(bin.binCode, bin.value ?? null);
  }
  return result;
}

/** Lineær interpolasjon mellom to RGB-kanaler, som to-sifret hex */
function lerpChannelHex(from: number, to: number, t: number): string {
  return Math.round(from + (to - from) * t).toString(16).padStart(2, '0');
}

/**
 * Felles fargeskala for alle innsikt-modi:
 * blå (#3b82f6) ved 0 → gul (#f59e0b) ved 0.5 → rød (#ef4444) ved 1.
 * Returnerer hex (#rrggbb) slik at modulens alpha-suffiks-mønster (farge + '4D') virker.
 */
export function insightColor(value: number): string {
  const v = Math.max(0, Math.min(1, value));
  const blue: [number, number, number] = [59, 130, 246];
  const amber: [number, number, number] = [245, 158, 11];
  const red: [number, number, number] = [239, 68, 68];

  const [from, to, t] = v <= 0.5
    ? [blue, amber, v * 2] as const
    : [amber, red, (v - 0.5) * 2] as const;

  return `#${lerpChannelHex(from[0], to[0], t)}${lerpChannelHex(from[1], to[1], t)}${lerpChannelHex(from[2], to[2], t)}`;
}
